import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { scheduleMessage } from "../../../redux/Slice/Inbox/ScheduledMessageSlice";
import { selectContactInfo } from "../../../redux/selectors";
import { getSocketId } from "../../../utils/socketHandler";
import toast from "react-hot-toast";
import { isSubscriptionBlockedError } from "../../../utils/subscriptionError";

const ScheduleMessageDialog = ({
  open,
  onClose,
  onMessageScheduled,
  messageType,
  messageContent,
  mediaFile,
  mediaFileName,
  templateData,
  templateCustomizations,
  contactId,
  AccountID,
}) => {
  const [scheduledTime, setScheduledTime] = useState(dayjs().add(1, "hour"));
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [windowEndTime, setWindowEndTime] = useState(null);

  const dispatch = useDispatch();
  const contactInfo = useSelector(selectContactInfo);

  // Calculate window end time from contact info
  useEffect(() => {
    if (contactInfo?.hoursRemaining !== undefined && contactInfo?.hoursRemaining > 0) {
      const now = new Date();
      const hoursRemaining = contactInfo.hoursRemaining;
      const windowEnd = new Date(now.getTime() + hoursRemaining * 60 * 60 * 1000);
      setWindowEndTime(windowEnd);
    }
  }, [contactInfo]);

  const handleSchedule = async () => {
    setError(null);

    // Validate scheduled time
    if (!scheduledTime || !scheduledTime.isValid()) {
      setError("Please select a valid date and time");
      return;
    }

    const selectedTime = scheduledTime.toDate();
    const now = new Date();

    if (selectedTime <= now) {
      setError("Scheduled time must be in the future");
      return;
    }

    // Check against window end time (skip for template messages)
    if (messageType !== "template" && windowEndTime && selectedTime > windowEndTime) {
      setError(
        `Scheduled time exceeds contact window. Window ends at ${dayjs(windowEndTime).format("MMM DD, YYYY hh:mm A")}`
      );
      return;
    }

    setLoading(true);

    try {
      const originSocketId = getSocketId();
      
      const scheduleData = {
        contactId,
        messageType,
        scheduledTime: selectedTime.toISOString(),
        originSocketId,
      };

      if (messageType === "text") {
        scheduleData.messageContent = messageContent;
      } else if (messageType === "template") {
        scheduleData.templateData = templateData;
        if (templateCustomizations) {
          scheduleData.templateCustomizations = templateCustomizations;
        }
      } else if (mediaFile) {
        scheduleData.mediaFile = mediaFile;
        if (mediaFileName) {
          scheduleData.mediaFileName = mediaFileName;
        }
      }

      await dispatch(scheduleMessage(scheduleData)).unwrap();
      
      toast.success("Message scheduled successfully!");
      
      // Call the callback to clear message content in TextBox
      if (onMessageScheduled) {
        onMessageScheduled();
      } else {
        onClose();
      }
    } catch (error) {
      setError(error || "Failed to schedule message");
      if (!isSubscriptionBlockedError(error)) {
        toast.error(error || "Failed to schedule message");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          m: { xs: 1, sm: 2 },
          width: { xs: "calc(100% - 16px)", sm: "auto" },
          maxHeight: { xs: "92vh", sm: "none" },
          // Was relying on the theme default (unset here) — now matches
          // every other dialog built this session (Rahim, 2026-08-24: "use
          // our new ui as well... corner radius").
          borderRadius: { xs: 2, sm: 3 },
        },
      }}
      sx={{
        // Same dialog-wide squared-button rule as TemplateSendBox.jsx.
        "& .MuiButton-root": { borderRadius: "8px" },
      }}
    >
      {/* Was fontSize responsively bumping to 1.25rem with no bottom border
          at all — flattened to the Section Title token (1rem/600) and given
          the same header/divider treatment as every other dialog this
          session (TemplateSendBox.jsx's own title, "Templates" heading). */}
      <DialogTitle
        sx={{
          fontSize: "1rem",
          fontWeight: 600,
          py: { xs: 1, sm: 1.5 },
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
        }}
      >
        Schedule Message
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 1.5, sm: 3 }, pb: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.25, sm: 2 }, mt: { xs: 1, sm: 1.5 } }}>
          {/* Alerts keep MUI's severity colors (real status semantics, not
              decorative) but corner radius bumped to match the rest of this
              pass — was the theme default (4px). */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: "8px" }}>
              {error}
            </Alert>
          )}

          {windowEndTime && messageType !== "template" && (
            <Alert severity="info" sx={{ borderRadius: "8px" }}>
              Contact window ends at: {dayjs(windowEndTime).format("MMM DD, YYYY hh:mm A")}
              {contactInfo?.hoursRemaining && (
                <Typography sx={{ display: "block", mt: 0.5, fontSize: "0.75rem" }}>
                  {contactInfo.hoursRemaining} hours remaining
                </Typography>
              )}
            </Alert>
          )}

          {messageType === "template" && (
            <Alert severity="success" sx={{ borderRadius: "8px" }}>
              Template messages can be scheduled at any time (no 24-hour window restriction)
            </Alert>
          )}

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              label="Schedule Date & Time"
              value={scheduledTime}
              onChange={(newValue) => {
                setScheduledTime(newValue);
                setError(null);
              }}
              minDateTime={dayjs()}
              maxDateTime={messageType !== "template" && windowEndTime ? dayjs(windowEndTime) : undefined}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!error,
                  // Rounded corners matching the app's other search/input
                  // fields (e.g. the Templates dialog's search bar) — was the
                  // theme default (4px).
                  sx: { "& .MuiOutlinedInput-root": { borderRadius: "10px" } },
                },
              }}
            />
          </LocalizationProvider>

          <Typography sx={{ color: "text.secondary", fontSize: "0.8125rem" }}>
            {messageType === "template"
              ? "The template message will be sent at the selected date and time. Template messages can be scheduled at any time."
              : "The message will be sent at the selected date and time. Make sure the time is within the 24-hour contact window."
            }
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexDirection: { xs: "column", sm: "row" }, gap: { xs: 1, sm: 0 }, px: { xs: 1.5, sm: 2 }, pb: { xs: 1.5, sm: 2 }, alignItems: "stretch" }}>
        {/* Cancel was a plain text-variant Button (no border at all) — every
            other Cancel button built this session is outlined. */}
        <Button variant="outlined" onClick={onClose} disabled={loading} sx={{ width: { xs: "100%", sm: "auto" }, order: { xs: 2, sm: 0 } }}>
          Cancel
        </Button>
        <Button onClick={handleSchedule} variant="contained" disabled={loading} sx={{ width: { xs: "100%", sm: "auto" }, order: { xs: 1, sm: 0 } }}>
          {loading ? "Scheduling..." : "Schedule"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleMessageDialog;

